/**
 * Braze's dismissed event says nothing about how a message was closed, so the close is observed here
 * and reported as `dismiss_type` on `iam_dismiss` (#261189).
 */

/** Documented `dismiss_type` values; CRM reporting keys on these strings. */
export const IAM_DISMISS_TYPES = {
  /** Shopper activated the message's own close ("X") control. */
  explicitClose: 'explicit_close',
  /** Any other shopper-driven close: overlay click, ESC, swipe, or an in-iframe control. */
  indirectClick: 'indirect_click',
  /** The message's own timer closed it with no shopper input. */
  autoDismiss: 'auto_dismiss',
} as const

export type IamDismissType = (typeof IAM_DISMISS_TYPES)[keyof typeof IAM_DISMISS_TYPES]

/** Braze renders each message inside its own root, with the close button nested in it. */
const IAM_ROOT_SELECTOR = '.ab-iam-root'
const CLOSE_BUTTON_SELECTOR = '.ab-close-button'

/** The SDK closes on these from `keydown` and stops propagation, so the button gets no click. */
const ACTIVATION_KEYS = ['Enter', ' ', 'Spacebar']

/** Roots whose close button was activated, kept apart so stacked messages cannot claim each other's. */
const closedRoots = new WeakSet<Element>()
let watcherCount = 0

const iamRoots = () => Array.from(document.querySelectorAll(IAM_ROOT_SELECTOR))

// Content cards render their own close button, so the enclosing message root is part of the test.
const recordClose = (target: EventTarget | null) => {
  if (!(target instanceof Element) || !target.closest(CLOSE_BUTTON_SELECTOR)) return
  const root = target.closest(IAM_ROOT_SELECTOR)
  if (root) closedRoots.add(root)
}

const onClick = (event: Event) => recordClose(event.target)

const onKeydown = (event: KeyboardEvent) => {
  if (ACTIVATION_KEYS.includes(event.key)) recordClose(event.target)
}

// Capture phase: the SDK stops propagation on the interactions that close a message.
const listen = (add: boolean) => {
  if (add) {
    document.addEventListener('click', onClick, true)
    document.addEventListener('keydown', onKeydown, true)
    return
  }
  document.removeEventListener('click', onClick, true)
  document.removeEventListener('keydown', onKeydown, true)
}

export type IamDismissalWatcher = {
  /** Ties the watcher to the root Braze just rendered; call straight after a successful display. */
  bindDisplayedRoot: () => void
  /** Starts the auto-dismiss clock; pass as Braze's `onDisplayCallback`, which starts its timer. */
  markDisplayed: () => void
  /** Custom HTML messages close from inside their iframe, where no DOM event reaches us. */
  markExplicitClose: () => void
  /** Drops the event for a close our own CTA handling triggered — already reported as `iam_click`. */
  suppress: () => void
  /** @returns The type to report, or `null` when the close was suppressed. */
  settle: () => IamDismissType | null
  /** Stops watching a message that raises no dismissal, such as one that was never displayed. */
  dispose: () => void
}

/**
 * Starts watching for the close of a single in-app message, before it is displayed so its root can
 * be told apart from any already on screen. Only the message's own close control and its timer are
 * attributed; every other dismissal reports `indirect_click`.
 *
 * @param autoDismissAfterMs - The message's own close timer, when it has one.
 * @param onClosed - Run when the message leaves the page, including the paths Braze reports nothing
 *   for, such as a CTA click-through or a render failure.
 * @returns The watcher; `settle()` from the dismissed subscriber, `dispose()` if never displayed.
 */
export function watchIamDismissalIntent(autoDismissAfterMs?: number, onClosed?: () => void): IamDismissalWatcher {
  const rootsBefore = iamRoots()
  let root: Element | null = null
  let timerStartedAt: number | undefined
  let watching = true
  let suppressed = false
  let explicitMark = false
  let explicitClose = false
  let removalObserver: MutationObserver | undefined

  if (watcherCount++ === 0) listen(true)

  // The SDK detaches the root before it reports the dismissal, so the interaction is taken here.
  const stopWatching = () => {
    if (!watching) return
    watching = false
    if (root) {
      explicitClose = closedRoots.has(root)
      closedRoots.delete(root)
    }
    removalObserver?.disconnect()
    if (--watcherCount === 0) listen(false)
  }

  return {
    bindDisplayedRoot: () => {
      if (!watching) return
      root = iamRoots().find((candidate) => !rootsBefore.includes(candidate)) ?? null
      if (!root?.parentNode) return

      removalObserver = new MutationObserver(() => {
        if (root?.isConnected) return
        stopWatching()
        onClosed?.()
      })
      removalObserver.observe(root.parentNode, { childList: true })
    },

    markDisplayed: () => {
      timerStartedAt = Date.now()
    },

    markExplicitClose: () => {
      explicitMark = watching
    },

    suppress: () => {
      suppressed = true
    },

    dispose: stopWatching,

    settle: () => {
      stopWatching()
      const timedOut =
        autoDismissAfterMs != null && timerStartedAt != null && Date.now() - timerStartedAt >= autoDismissAfterMs

      if (suppressed) return null
      if (explicitMark || explicitClose) return IAM_DISMISS_TYPES.explicitClose
      if (timedOut) return IAM_DISMISS_TYPES.autoDismiss
      return IAM_DISMISS_TYPES.indirectClick
    },
  }
}
